import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatRelative, formatWhen, lifecycleLabel, machineDisplayName } from "@/lib/format";
import { ApiClientError, type Machine, type OperatorOverview } from "@/lib/types";
import { AuthStatePanels } from "@/components/AuthState";
import { Button } from "@/components/Button";
import { ButtonLink } from "@/components/ButtonLink";
import { ConfirmSheet } from "@/components/ConfirmSheet";
import { DemoChip } from "@/components/DemoChip";
import { Field, TextArea } from "@/components/Field";
import { StatePanel } from "@/components/StatePanel";
import { StatusRail } from "@/components/StatusRail";
import { useLocale } from "@/locale/LocaleContext";
import shell from "@/components/shell.module.css";
import ui from "@/components/ui.module.css";

export function OperatorOverviewPage() {
  const { m, locale } = useLocale();
  const [data, setData] = useState<OperatorOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [target, setTarget] = useState<Machine | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const overview = await api.operatorOverview();
      setData(overview);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiClientError ? e : new ApiClientError(0, "error", m.states.error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onMaintenance() {
    if (!target || reason.trim().length < 8) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.setMaintenance(target.id, { enabled, reason: reason.trim() });
      setTarget(null);
      setReason("");
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : m.operator.actionFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthStatePanels loading={loading} error={error}>
      {!data ? (
        <StatePanel kind="empty" title={m.states.empty} />
      ) : (
        <div>
          <h1 className={shell.pageTitle}>{m.operator.title}</h1>
          <p className={shell.pageLead}>{m.operator.lead}</p>

          <div className={ui.panel} style={{ marginBottom: "1.25rem" }}>
            <h2 className="display" style={{ margin: 0, fontSize: "1.05rem" }}>
              {m.operatorWorkspace.queueTitle}
            </h2>
            <p className={ui.meta} style={{ marginTop: "0.35rem" }}>
              {m.operatorWorkspace.queueLead}
            </p>
            <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
              <ButtonLink to="/operator/workspace-requests" variant="secondary">
                {m.operatorWorkspace.openQueue}
              </ButtonLink>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(8rem, 100%), 1fr))",
              gap: "0.75rem",
              marginBottom: "1.25rem",
            }}
          >
            <div className={ui.panel}>
              <div className={ui.meta}>{m.operator.users}</div>
              <div className="display" style={{ fontSize: "1.5rem" }}>
                {data.counts.users}
              </div>
            </div>
            <div className={ui.panel}>
              <div className={ui.meta}>{m.operator.tenants}</div>
              <div className="display" style={{ fontSize: "1.5rem" }}>
                {data.counts.tenants}
              </div>
            </div>
            <div className={ui.panel}>
              <div className={ui.meta}>{m.operator.tasks}</div>
              <div className="display" style={{ fontSize: "1.5rem" }}>
                {data.counts.tasks}
              </div>
            </div>
          </div>

          <h2 className="display" style={{ fontSize: "1.1rem" }}>
            {m.operator.machines}
          </h2>
          {data.machines.map((machine) => (
            <div key={machine.id} className={ui.row} style={{ cursor: "default" }}>
              <StatusRail state={machine.lifecycle} />
              <div style={{ width: "100%" }}>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                  <strong>{machineDisplayName(m, machine)}</strong>
                  {machine.mode === "demo" ? <DemoChip /> : <DemoChip label="LIVE" />}
                </div>
                <div className={ui.meta}>
                  {machine.model ?? m.machines.modelUnknown} · {lifecycleLabel(m, machine.lifecycle)} ·{" "}
                  {lifecycleLabel(m, machine.health)} · {formatRelative(machine.last_seen_at, locale)}
                </div>
                <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.55rem" }}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setEnabled(machine.lifecycle !== "maintenance");
                      setReason("");
                      setActionError(null);
                      setTarget(machine);
                    }}
                  >
                    {machine.lifecycle === "maintenance"
                      ? m.operator.maintenanceOff
                      : m.operator.maintenanceOn}
                  </Button>
                </div>
              </div>
            </div>
          ))}

          <h2 className="display" style={{ fontSize: "1.1rem", marginTop: "1.5rem" }}>
            {m.operator.events}
          </h2>
          {data.recent_events.length === 0 ? (
            <p className={ui.meta}>{m.operator.noEvents}</p>
          ) : (
            data.recent_events.map((ev) => (
              <div key={ev.id} style={{ padding: "0.45rem 0", borderBottom: "1px solid var(--color-line)" }}>
                <div>{ev.action}</div>
                <div className={`mono ${ui.meta}`}>{formatWhen(ev.created_at, locale)}</div>
              </div>
            ))
          )}

          <ConfirmSheet
            open={Boolean(target)}
            title={m.operator.confirmTitle}
            confirmLabel={m.operator.confirmAction}
            confirmDisabled={reason.trim().length < 8}
            busy={busy}
            onClose={() => setTarget(null)}
            onConfirm={() => void onMaintenance()}
          >
            <p>{m.operator.confirmBody}</p>
            {target ? <p className={ui.meta}>{machineDisplayName(m, target)}</p> : null}
            <Field label={m.operator.reason} htmlFor="maint-reason" hint={m.operator.reasonRequired}>
              <TextArea
                id="maint-reason"
                className="resize-none"
                style={{ resize: "none" }}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
            {actionError ? <p className={ui.fieldError}>{actionError}</p> : null}
          </ConfirmSheet>
        </div>
      )}
    </AuthStatePanels>
  );
}
